import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import MotifMenu, { MotifMenuItem } from "@ey-xd/motif-react/Menu";
import { MotifCheckbox, MotifButton, MotifLabel } from "@ey-xd/motif-react";
import "../partnerFileUpload/PartnerUploadFilter.scss";

const EMPTY_OPTIONS = [];

const AdminExportExcelPanel = ({
  show,
  onClose,
  exportOptions = EMPTY_OPTIONS,
  onApply,
  isExporting = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const prevShowRef = useRef(show);

  useEffect(() => {
    const wasHidden = !prevShowRef.current;
    prevShowRef.current = show;

    if (show && wasHidden) {
      setSearchValue("");
      setExpandedAccordions([]);
      const cleared = exportOptions.reduce((acc, category) => {
        acc[category.key] = [];
        return acc;
      }, {});
      setSelectedOptions(cleared);
    }
  }, [show, exportOptions]);

  useEffect(() => {
    setSelectedOptions((prev) => {
      const next = {};
      let changed = false;
      exportOptions.forEach((category) => {
        next[category.key] = prev[category.key] || [];
        if (next[category.key] !== prev[category.key]) changed = true;
      });
      const prevKeys = Object.keys(prev);
      if (prevKeys.length !== Object.keys(next).length) changed = true;
      return changed ? next : prev;
    });
  }, [exportOptions]);

  const filteredCategories = useMemo(() => {
    const search = searchValue.toLowerCase();
    return exportOptions
      .map((category) => ({
        ...category,
        options: (category.options || [])
          .filter((option) => String(option.label).toLowerCase().includes(search))
          .sort((a, b) => String(a.label).localeCompare(String(b.label))),
      }))
      .filter((category) => category.options.length > 0);
  }, [exportOptions, searchValue]);

  useEffect(() => {
    if (searchValue.length >= 2) {
      const keys = exportOptions
        .filter((category) =>
          (category.options || []).some((option) =>
            String(option.label).toLowerCase().includes(searchValue.toLowerCase())
          )
        )
        .map((category) => category.key);
      setExpandedAccordions(keys);
    }
  }, [searchValue, exportOptions]);

  const isAllCleared = () =>
    Object.values(selectedOptions).every((values) => (values || []).length === 0);

  const handleToggleSelectAll = (categoryKey) => {
    const category = filteredCategories.find((item) => item.key === categoryKey);
    const optionIds = (category?.options || []).map((option) => option.id);
    const current = selectedOptions[categoryKey] || [];
    const areAllSelected = optionIds.length > 0 && current.length === optionIds.length;

    setSelectedOptions((prev) => ({
      ...prev,
      [categoryKey]: areAllSelected ? [] : optionIds,
    }));
  };

  const handleOptionToggle = (categoryKey, optionId) => {
    const current = selectedOptions[categoryKey] || [];
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];

    setSelectedOptions((prev) => ({
      ...prev,
      [categoryKey]: next,
    }));
  };

  return (
    <MotifMenu show={show} onClose={onClose} className="motif-menu-container">
      <div className="motif-menu-container__header">
        <MotifMenuItem disabled className="motif-menu-container__header-item">
          <div className="motif-menu-container__header-icon">
            <img
              src={require("../../common/images/excel.png")}
              alt="Export Icon"
              style={{ width: "16px", height: "16px" }}
            />
          </div>
          <div>
            Export Excel
            <div className="motif-menu-container__header-subtitle">
              Select values to include in export
            </div>
          </div>
        </MotifMenuItem>
      </div>

      <div className="motif-menu-container__content">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon className="search-icon" />,
          }}
          size="small"
          className="search-field"
        />

        <div className="motif-menu-container__content-accordions">
          {filteredCategories.map((category) => {
            const isExpanded = expandedAccordions.includes(category.key);
            const selectedCount = (selectedOptions[category.key] || []).length;
            return (
              <Accordion
                key={category.key}
                disableGutters
                elevation={0}
                square
                expanded={isExpanded}
                onChange={(_, expanded) => {
                  setExpandedAccordions((prev) =>
                    expanded
                      ? [...prev, category.key]
                      : prev.filter((key) => key !== category.key)
                  );
                }}
                className="accordion"
              >
                <AccordionSummary
                  className="accordion-summary"
                  expandIcon={<ExpandMoreIcon style={{ fontSize: "20px" }} />}
                  sx={{
                    minHeight: "40px",
                    height: "40px",
                    padding: "0 8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <MotifLabel className="motif-menu-container__content-accordion-label">
                    {category.category}
                    {selectedCount > 0 ? ` (${selectedCount})` : ""}
                  </MotifLabel>
                </AccordionSummary>

                <AccordionDetails className="motif-menu-container__content-accordion-details">
                  <div className="motif-menu-container__content-accordion-selectall">
                    <MotifCheckbox
                      id={`select-all-${category.key}`}
                      label="Select All"
                      checked={selectedCount === category.options.length && category.options.length > 0}
                      onChange={() => handleToggleSelectAll(category.key)}
                      className="motif-menu-container__content-accordion-selectall-checkbox"
                    />
                    <span className="selectAll-text">Select All</span>
                  </div>
                  <hr className="motif-menu-container__content-accordion-divider" />

                  <div className="motif-menu-container__content-accordion-options">
                    {category.options.map((option) => (
                      <div
                        className="motif-menu-container__content-accordion-option"
                        key={option.id}
                      >
                        <MotifCheckbox
                          id={`option-${category.key}-${option.id}`}
                          label={option.label}
                          checked={(selectedOptions[category.key] || []).includes(option.id)}
                          onChange={() => handleOptionToggle(category.key, option.id)}
                          className="motif-menu-container__content-accordion-option-checkbox"
                        />
                        <span className="selectAll-text">{option.label}</span>
                      </div>
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </div>

      <div className="motif-menu-container__footer">
        <hr className="motif-menu-container__footer-divider" />

        <div className="motif-menu-container__footer-buttons">
          <MotifButton
            className="motif-menu-container__footer-clear-button"
            disabled={isAllCleared() || isExporting}
            onClick={() => {
              setSearchValue("");
              const cleared = exportOptions.reduce((acc, category) => {
                acc[category.key] = [];
                return acc;
              }, {});
              setSelectedOptions(cleared);
            }}
          >
            Clear All
          </MotifButton>

          <MotifButton
            className="motif-menu-container__footer-apply-button"
            onClick={() => {
              onApply(selectedOptions);
              setSearchValue("");
              onClose();
            }}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Apply"}
          </MotifButton>
        </div>
      </div>
    </MotifMenu>
  );
};

export default AdminExportExcelPanel;
